"use client";

export default function CookiesPolicy() {
  return (
    <div className="relative min-h-screen bg-white py-32">

      <style jsx global>{`
        #global-contact-form { display: none !important; }
      `}</style>

      {/* Background decorative lines */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="w-full origin-center transform-gpu absolute -left-[40%] top-0">
          <div className="h-[1px] bg-gray-300 rotate-[296.7deg] w-[200%]" />
        </div>
        <div className="w-full origin-center transform-gpu absolute -right-[40%] top-0">
          <div className="h-[1px] bg-gray-300 rotate-[296.8deg] w-[200%]" />
        </div>
      </div>

      <div className="custom-container mx-auto px-4 relative z-10 max-w-7xl">

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-bold text-[#272d57] tracking-tight">
            Cookies Policy
          </h1>
          <p className="text-gray-500 mt-2">Last updated: November 13, 2025</p>
        </div>

        {/* Content Container */}
        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed border shadow-[0_4px_54px_0_rgba(0,0,0,0.05)] border-gray-100 p-12 rounded-lg">

          {/* INTRODUCTION */}
          <p>
            At MakeMyBrand Labs, your privacy and online experience are our top priorities. We are committed to being transparent about how and why we use cookies to enhance your browsing experience. This Cookie Policy explains what cookies are, how we use them, and how you can control them.
          </p>
          <p>
            By using{" "}
            <a
              href="https://makemybrandlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            >
              MakeMyBrandLabs.com
            </a>{" "}
            you consent to the use of cookies in accordance with this policy.
          </p>

          {/* 1. WHAT ARE COOKIES */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            1. What Are Cookies?
          </h2>

          <p>
            Cookies are small text files stored on your computer, smartphone, or tablet when you visit a website. They help websites remember your preferences, improve site performance, and deliver a more personalized experience.
          </p>

          <p className="my-5">Cookies can be categorized as follows:</p>

          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed">
            <li><strong>Session Cookies</strong>: Temporary cookies that remain active only during your current visit and are deleted when you close your browser.            </li>
            <li><strong>Persistent Cookies</strong>: Stored on your device for a defined period and help remember your settings or preferences for future visits.</li>
            <li><strong>First-Party Cookies</strong>:  Set directly by MakeMyBrandLabs.com to ensure the proper functioning of our website.            .</li>
            <li><strong>Third-Party Cookies</strong>: Set by external services or partners for analytics, advertising, or social media integration.            </li>
          </ul>

          {/* 2. TYPES OF COOKIES WE USE */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            2. Types of Cookies We Use
          </h2>

          <h3 className="text-xl font-semibold text-[#272d57] mt-6 mb-2">
            a. Strictly Necessary Cookies
          </h3>
          <p>
            These are essential for our website to function correctly. They enable features like secure browsing, login authentication, and form submission. Without these cookies, certain services on our website may not work properly.

          </p>

          <h3 className="text-xl font-semibold text-[#272d57] mt-6 mb-2">
            b. Performance & Analytics Cookies
          </h3>
          <p>
            These cookies help us understand how users interact with our website by collecting anonymous data about page visits, time spent, and any issues encountered. We use this data to enhance usability and performance.
            Our primary analytics partner is Google Analytics.
          </p>
          <p>Our primary analytics partner is Google Analytics.</p>

          <h3 className="text-xl font-semibold text-[#272d57] mt-6 mb-2">
            c. Functionality Cookies
          </h3>
          <p>
            These remember your preferences — such as language, region, or previous selections — to provide a more tailored experience when you return to our site.

          </p>

          <h3 className="text-xl font-semibold text-[#272d57] mt-6 mb-2">
            d. Targeting & Advertising Cookies
          </h3>
          <p>
            Used to deliver relevant advertising and measure the effectiveness of campaigns. These cookies may track your browsing behavior across different websites to show personalized ads.

          </p>
          <p>
            We may work with trusted advertising partners such as Google Ads and LinkedIn Ads for this purpose.
          </p>

          {/* 3. WHY WE USE COOKIES */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            3. Why We Use Cookies
          </h2>
          <p>We use cookies for the following reasons:</p>
          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed mt-4">
            <li>Ensure the website operates efficiently and securely.</li>
            <li>Improve performance, navigation, and usability.</li>
            <li>Analyze user behavior to enhance content and design.</li>
            <li>Personalize user experience.</li>
            <li>Deliver marketing and advertising relevant to your interests.</li>
          </ul>


          {/* 4. THIRD-PARTY COOKIES */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            4. Third-Party Cookies
          </h2>

          <p>
            We collaborate with trusted partners who may set cookies to help us analyze traffic, improve user experience, or display relevant ads.
          </p>

          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed mt-4">
            <li><strong>Google Analytics –</strong> for website usage insights</li>
            <li><strong>Google Ads & LinkedIn Ads –</strong> for remarketing and campaign optimization</li>
          </ul>

          <p className="mt-3">
            These third-party cookies are governed by the respective provider’s privacy and cookie policies.
          </p>

          {/* 5. HOW TO MANAGE COOKIES */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            5. How to Manage Cookies
          </h2>

          <p>You can control and manage cookies in several ways:          </p>

          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed">
            <li>
              <strong>Cookie Consent Banner</strong>:When you visit our site, you can choose to accept, reject, or customize cookie settings.

            </li>
            <li>
              <strong>Browser Settings</strong>: Most browsers allow you to view, delete, or block cookies. You can adjust these settings through your browser’s “Privacy” or “Security” section.

            </li>
          </ul>

          <p className="mt-3">
            Please note: Disabling certain cookies may affect website functionality or limit your experience.

          </p>

          {/* 6. GDPR COMPLIANCE */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            6. GDPR Compliance (European Union Users)
          </h2>

          <p className="my-2">
            If you are located in the European Economic Area (EEA), your data is protected under the General Data Protection Regulation (GDPR).
          </p>

          <p>Under GDPR, you have the following rights regarding your personal data collected through cookies:
          </p>
          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed mt-4">
            <li>
              <strong>Right to Access:</strong> You may request a copy of the data we hold about you.
            </li>

            <li>
              <strong>Right to Rectification:</strong> You may ask to correct inaccurate or incomplete data.
            </li>

            <li>
              <strong>Right to Erasure:</strong> You may request deletion of data collected via cookies (where applicable).
            </li>

            <li>
              <strong>Right to Restrict Processing:</strong> You may request limited use of your data.
            </li>

            <li>
              <strong>Right to Data Portability:</strong> You may receive your data in a structured, commonly used format.
            </li>

            <li>
              <strong>Right to Withdraw Consent:</strong> You can withdraw your consent for cookie usage at any time by adjusting your cookie settings.
            </li>
          </ul>


          <p className="mt-3">
            To exercise your GDPR rights or make a privacy-related request, please contact us at {" "}
            <a
              href="mailto:info@makemybrandlabs.com"
              className="font-medium text-blue-600 hover:underline"
            >
              info@makemybrandlabs.com
            </a>.
          </p>

          {/* 7. CCPA COMPLIANCE */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            7. CCPA Compliance (California Users)
          </h2>

          <p>
            If you are a resident of <strong> California (USA), the California Consumer Privacy Act (CCPA) </strong> grants you the following rights related to cookies and tracking technologies:

          </p>

          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed mt-4">
            <li><strong>Right to Know:</strong> You may request details about the personal information we collect through cookies.</li>
            <li><strong>Right to Delete:</strong> You may ask us to delete your personal data collected via cookies.</li>
            <li><strong>Right to Opt-Out of Sale/Sharing:</strong> You may opt out of the sale or sharing of your personal information.</li>
            <li><strong>Right to Non-Discrimination:</strong> You cannot be discriminated against for exercising your CCPA rights.</li>
          </ul>

          <p className="mt-3">
            To make a CCPA request or to opt out, please contact{" "}
            <a
              href="mailto:info@makemybrandlabs.com"
              className="font-medium text-blue-600 hover:underline"
            >
              info@makemybrandlabs.com
            </a>.
          </p>

          {/* 8. UPDATES */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-3">
            8. Updates to This Policy
          </h2>

          <p>
            We may update this Cookie Policy from time to time to reflect changes in technology, law, or our business practices. When we make updates, the “Last Updated” date at the top of this page will change. We encourage you to review this page periodically.

          </p>

          {/* 9. CONTACT */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            9. Contact Us
          </h2>

          <p>If you have any questions about this Cookie Policy or your data privacy rights, please contact us:
          </p>

          <p className="mt-2 leading-relaxed">
            <strong>MMB Digital Labs (Make My Brand Labs)</strong><br />
            📧{" "}
            <a
              href="mailto:info@makemybrandlabs.com"
              className="font-medium text-blue-600 hover:underline"
            >
              info@makemybrandlabs.com
            </a>
            <br />
            🌐{" "}
            <a
              href="https://makemybrandlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            >
              makemybrandlabs.com
            </a>
          </p>

        </div>
      </div>
    </div>
  );
}
